import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyCn = { cn_no: "", from_location: "", to_location: "", packages: "", weight: "", freight: "0" };
const emptyForm = {
  date: new Date().toISOString().slice(0,10),
  truck_no: "", owner_name: "", owner_phone: "", owner_pan: "",
  challan_no: "", lorry_provider_name: "", lorry_provider_address: "",
  driver_name: "", driver_phone: "", driver_license: "",
  from_location: "", to_location: "",
  cns: [ { ...emptyCn } ],
  total_freight: "0", advance: "0", balance: "0",
  freight: "0", detention: "0", charge1: "0", charge2: "0",
  total_amount: "0", bank_advance: "0", cash_advance: "0", balance_payable: "0",
  remarks: "",
};
const n = v => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };

export default function MemoForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/trucks").then(({data})=>setTrucks(data)).catch(()=>{});
    api.get("/drivers").then(({data})=>setDrivers(data)).catch(()=>{});
    if (isEdit) api.get(`/memos/${id}`).then(({data})=>setForm({...emptyForm, ...data}));
  }, [id, isEdit]);

  useEffect(() => {
    const tf = form.cns.reduce((s,c)=>s + n(c.freight), 0);
    const bal = tf - n(form.advance);
    setForm(f=>({...f, total_freight: tf.toFixed(2), balance: bal.toFixed(2)}));
    // eslint-disable-next-line
  }, [JSON.stringify(form.cns), form.advance]);

  useEffect(() => {
    const total = n(form.freight) + n(form.detention) + n(form.charge1) + n(form.charge2);
    const payable = total - n(form.bank_advance) - n(form.cash_advance);
    setForm(f=>({...f, total_amount: total.toFixed(2), balance_payable: payable.toFixed(2)}));
    // eslint-disable-next-line
  }, [form.freight, form.detention, form.charge1, form.charge2, form.bank_advance, form.cash_advance]);

  const set = (k) => (e) => setForm(f=>({...f, [k]: e.target?.value ?? e}));
  const setCn = (i,k,v) => setForm(f=>({...f, cns: f.cns.map((c,ix)=>ix===i?{...c,[k]:v}:c)}));
  const addCn = () => setForm(f=>({...f, cns: [...f.cns, {...emptyCn}]}));
  const removeCn = (i) => setForm(f=>({...f, cns: f.cns.filter((_,ix)=>ix!==i)}));

  // Auto-fill a CN row (and empty header fields) from the matching consignment record.
  const fetchCn = async (i) => {
    const cnNo = (form.cns[i]?.cn_no || "").trim();
    if (!cnNo) return;
    try {
      const { data: c } = await api.get(`/consignments/by-cn`, { params: { cn_no: cnNo } });
      const t = trucks.find(x => x.number === c.truck_no);
      setForm(f => ({
        ...f,
        cns: f.cns.map((row, ix) => ix === i ? {
          ...row,
          cn_no: c.cn_no,
          from_location: c.from_location || "",
          to_location: c.to_location || "",
          packages: c.no_pkgs || "",
          weight: c.charged_weight || c.actual_weight || "",
          freight: c.grand_total || c.total || "0",
        } : row),
        // Fill header fields only when still empty (never overwrite user edits).
        truck_no: f.truck_no || c.truck_no || "",
        owner_name: f.owner_name || t?.owner_name || "",
        owner_phone: f.owner_phone || t?.owner_phone || "",
        driver_name: f.driver_name || c.driver_name || "",
        driver_phone: f.driver_phone || c.driver_phone || "",
        from_location: f.from_location || c.from_location || "",
        to_location: f.to_location || c.to_location || "",
      }));
      toast.success(`CN ${c.cn_no} details loaded`);
    } catch (err) {
      if (err.response?.status === 404) toast.error(`CN ${cnNo} not found`);
      else toast.error("Failed to fetch CN details");
    }
  };

  const pickTruck = (num) => {
    const t = trucks.find(x=>x.number===num);
    setForm(f=>({...f, truck_no: num, owner_name: t?.owner_name ?? f.owner_name, owner_phone: t?.owner_phone ?? f.owner_phone}));
  };
  const pickDriver = (nm) => {
    const d = drivers.find(x=>x.name===nm);
    setForm(f=>({...f, driver_name: nm, driver_phone: d?.phone ?? f.driver_phone, driver_license: d?.license_no ?? f.driver_license}));
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const res = isEdit ? await api.put(`/memos/${id}`, form) : await api.post("/memos", form);
      toast.success(isEdit? "Updated" : `Created ${res.data.memo_no}`);
      navigate(`/portal/memos/${res.data.id}/print`);
    } catch(err){ toast.error(formatApiError(err.response?.data?.detail)); }
    finally{ setSaving(false); }
  };

  return (
    <div className="p-8" data-testid="memo-form-page">
      <h1 className="font-display text-3xl text-slate-900 mb-6">{isEdit ? "Edit Freight Memo" : "New Freight Memo"}</h1>
      <form onSubmit={onSubmit} className="space-y-6">
        <section className="bg-white p-5 rounded-xl border border-slate-100 grid grid-cols-2 gap-4">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} required data-testid="memo-date"/></div>
          <div><Label>Challan No.</Label><Input value={form.challan_no} onChange={set("challan_no")} data-testid="memo-challan"/></div>
          <div><Label>Truck No.</Label>
            <Input list="trucks" value={form.truck_no} onChange={(e)=>pickTruck(e.target.value)} data-testid="memo-truck"/>
            <datalist id="trucks">{trucks.map(t=><option key={t.id} value={t.number}/>)}</datalist>
          </div>
          <div><Label>Owner Name</Label><Input value={form.owner_name} onChange={set("owner_name")}/></div>
          <div><Label>Owner Phone</Label><Input value={form.owner_phone} onChange={set("owner_phone")}/></div>
          <div><Label>Owner PAN</Label><Input value={form.owner_pan} onChange={set("owner_pan")} data-testid="memo-owner-pan"/></div>
          <div><Label>Lorry Provider (Name)</Label><Input value={form.lorry_provider_name} onChange={set("lorry_provider_name")} data-testid="memo-lorry-provider-name"/></div>
          <div><Label>Lorry Provider (Address)</Label><Input value={form.lorry_provider_address} onChange={set("lorry_provider_address")} data-testid="memo-lorry-provider-address"/></div>
          <div><Label>Driver Name</Label>
            <Input list="drivers" value={form.driver_name} onChange={(e)=>pickDriver(e.target.value)}/>
            <datalist id="drivers">{drivers.map(d=><option key={d.id} value={d.name}/>)}</datalist>
          </div>
          <div><Label>Driver Phone</Label><Input value={form.driver_phone} onChange={set("driver_phone")}/></div>
          <div><Label>Driver License</Label><Input value={form.driver_license} onChange={set("driver_license")}/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>From</Label><Input value={form.from_location} onChange={set("from_location")}/></div>
            <div><Label>To</Label><Input value={form.to_location} onChange={set("to_location")}/></div>
          </div>
        </section>

        <section className="bg-white p-5 rounded-xl border border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Consignments in this trip</h3>
            <button type="button" onClick={addCn} className="text-sm text-[#0b5cc4] hover:underline inline-flex items-center gap-1"><Plus size={14}/> Add CN</button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-2">CN No</th>
                <th className="text-left px-2 py-2">From</th>
                <th className="text-left px-2 py-2">To</th>
                <th className="text-left px-2 py-2">Pkgs</th>
                <th className="text-left px-2 py-2">Wt(Kg)</th>
                <th className="text-right px-2 py-2">Freight (₹)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.cns.map((c,i)=>(
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-1 py-1"><Input value={c.cn_no} onChange={(e)=>setCn(i,"cn_no",e.target.value)} onBlur={()=>fetchCn(i)} onKeyDown={(e)=>{ if(e.key==="Enter"){ e.preventDefault(); fetchCn(i); } }} placeholder="Enter CN No" data-testid={`memo-cn-no-${i}`}/></td>
                  <td className="px-1 py-1"><Input value={c.from_location} onChange={(e)=>setCn(i,"from_location",e.target.value)}/></td>
                  <td className="px-1 py-1"><Input value={c.to_location} onChange={(e)=>setCn(i,"to_location",e.target.value)}/></td>
                  <td className="px-1 py-1"><Input value={c.packages} onChange={(e)=>setCn(i,"packages",e.target.value)}/></td>
                  <td className="px-1 py-1"><Input value={c.weight} onChange={(e)=>setCn(i,"weight",e.target.value)}/></td>
                  <td className="px-1 py-1"><Input value={c.freight} onChange={(e)=>setCn(i,"freight",e.target.value)} className="text-right"/></td>
                  <td className="px-1 py-1"><button type="button" onClick={()=>removeCn(i)} className="p-2 hover:bg-red-50 rounded text-red-600"><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="bg-white p-5 rounded-xl border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Freight &amp; Calculation</h3>
            <button type="button" onClick={()=>setForm(f=>({...f, freight: f.total_freight}))}
              className="text-xs text-[#0b5cc4] hover:underline" data-testid="memo-use-cn-total-btn">
              Use CN Total (₹{form.total_freight})
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><Label>Freight (₹)</Label><Input value={form.freight} onChange={set("freight")} inputMode="decimal" data-testid="memo-freight"/></div>
            <div><Label>Detention (₹)</Label><Input value={form.detention} onChange={set("detention")} inputMode="decimal" data-testid="memo-detention"/></div>
            <div><Label>Charges 1 (₹)</Label><Input value={form.charge1} onChange={set("charge1")} inputMode="decimal" data-testid="memo-charge1"/></div>
            <div><Label>Charges 2 (₹)</Label><Input value={form.charge2} onChange={set("charge2")} inputMode="decimal" data-testid="memo-charge2"/></div>
            <div><Label className="font-semibold">TOTAL (₹)</Label><Input value={form.total_amount} readOnly className="font-semibold bg-slate-50" data-testid="memo-total-amount"/></div>
            <div><Label>Less Bank Advance (₹)</Label><Input value={form.bank_advance} onChange={set("bank_advance")} inputMode="decimal" data-testid="memo-bank-advance"/></div>
            <div><Label>Less Cash Advance (₹)</Label><Input value={form.cash_advance} onChange={set("cash_advance")} inputMode="decimal" data-testid="memo-cash-advance"/></div>
            <div><Label className="font-semibold">BALANCE PAYABLE (₹)</Label><Input value={form.balance_payable} readOnly className="font-bold bg-amber-50 text-[#0b3a7a]" data-testid="memo-balance-payable"/></div>
          </div>
          <div className="mt-4"><Label>Remarks</Label><Textarea value={form.remarks} onChange={set("remarks")} rows={2}/></div>
        </section>

        <Button type="submit" className="btn-primary" disabled={saving} data-testid="memo-save-btn">{saving?"Saving…":(isEdit?"Save Changes":"Save & Print")}</Button>
      </form>
    </div>
  );
}
